import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, Droplets, Wrench, Waves, ShieldAlert, PhoneCall, Pipette } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface ProblemCategory {
  id: string;
  title: string;
  description: string;
  badge: string;
  iconName: string;
  link: string;
  urgency: "high" | "medium" | "critical";
}
const problemCategories: ProblemCategory[] = [{
  id: "burst-pipe",
  title: "Burst & Leaking Pipes",
  description: "Active line breaks, pinhole leaks inside drywall, slab leaks, and sudden drops in water pressure across your property.",
  badge: "24/7 Priority Dispatch",
  iconName: "pipette",
  link: "/problems/",
  urgency: "critical"
}, {
  id: "water-heaters",
  title: "Water Heater Breakdown",
  description: "No hot water, leaking tank bottoms, loud knocking noises, and malfunctioning pilot lights or heating elements.",
  badge: "Same-Day Repair",
  iconName: "flame",
  link: "/water-heater/",
  urgency: "high"
}, {
  id: "clogged-drains",
  title: "Severe Drain Backups",
  description: "Multi-fixture clogs, standing water in showers, slow sink drainage, and foul sewer smells returning into fixtures.",
  badge: "Rapid Clearing",
  iconName: "waves",
  link: "/problems/",
  urgency: "high"
}, {
  id: "sewer-lines",
  title: "Main Sewer Line Failures",
  description: "Yard puddles with wastewater, multiple toilets bubbling, or whole-house backups requiring prompt camera inspection.",
  badge: "Critical Service",
  iconName: "alert-triangle",
  urgency: "critical",
  link: "/services/"
}, {
  id: "overflowing-toilets",
  title: "Toilet & Fixture Flooding",
  description: "Constant overflowing bowls, non-stop tank filling, cracked porcelain, and broken shut-off valves leaking onto floors.",
  badge: "Immediate Action",
  iconName: "droplets",
  link: "/problems/",
  urgency: "medium"
}, {
  id: "gas-plumbing",
  title: "Gas Line & Valve Faults",
  description: "Suspected gas smells near water heaters, faulty shut-off switches, or uncertified line extensions needing certified checks.",
  badge: "Emergency Priority",
  iconName: "shield-alert",
  urgency: "critical",
  link: "/services/"
}];
export default function RelatedProblems() {
  const renderCategoryIcon = (icon: string) => {
    switch (icon) {
      case "pipette":
        return <Pipette className="size-6 text-primary" />;
      case "flame":
        return <Flame className="size-6 text-accent" />;
      case "waves":
        return <Waves className="size-6 text-chart-3" />;
      case "alert-triangle":
        return <AlertTriangle className="size-6 text-destructive" />;
      case "droplets":
        return <Droplets className="size-6 text-primary" />;
      case "shield-alert":
        return <ShieldAlert className="size-6 text-destructive" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header content */}
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-primary">
            Diagnostic & Emergency Directory
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Explore Related Plumbing Emergencies
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Identify your specific situation below to understand the risk, get immediate shutoff instructions, and connect directly with a Jonesboro technician.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {problemCategories.map((category, index) => <div key={category.id} data-index={index} className="h-full">
              <Card className="flex h-full flex-col justify-between border border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex size-12 items-center justify-center rounded-lg bg-secondary">
                        {renderCategoryIcon(category.iconName)}
                      </div>
                      <Badge variant={category.urgency === "critical" ? "destructive" : "secondary"} className="text-xs font-semibold">
                        {category.badge}
                      </Badge>
                    </div>
                    <CardTitle className="mt-4 text-xl font-bold text-card-foreground">
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {category.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <div className="p-6 pt-0">
                  <Button asChild variant="outline" className="w-full justify-between font-medium group text-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Link to={category.link}>
                      <span>View problem guide</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>)}
        </div>

        {/* Emergency Help Banner */}
        <div className="mt-14 rounded-2xl border border-border bg-secondary p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="space-y-2 text-center md:text-left">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-destructive">
                <AlertTriangle className="size-4" /> Urgent Support Needed?
              </span>
              <h3 className="text-xl font-bold text-secondary-foreground sm:text-2xl">
                Can't find your exact plumbing problem?
              </h3>
              <p className="max-w-xl text-sm text-muted-foreground sm:text-base">
                Our Jonesboro emergency dispatchers are on standby 24 hours a day to assess water damage risks and route an equipped van to your door.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                <Link to="/contact/">
                  <PhoneCall className="mr-2 size-4" />
                  Contact Dispatch
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border bg-background hover:bg-accent font-medium">
                <Link to="/services/">
                  All Services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}